import {BlockerContext, Separator, showNotification} from 'platform/components';
import {z} from 'zod';

import {useCallback} from 'react';

import i18n from '@dms/i18n';
import {settingsRoutes, testIds} from '@dms/routes';
import {handleApiError} from '@dms/shared';
import {
  createVehicleSeriesDefinition,
  useApiDispatch,
  VehicleSeriesDefinitionRequestBody,
} from '@dms/teas';

import {useNavigate} from 'shared/web';

import {VehicleSeriesDefinitionFormType} from '../types';
import {VehicleSeriesDefinitionFormValidations} from './validations';
import {VehicleSeriesDefinitionFormButtons} from './VehicleSeriesDefinitionFormButtons';
import {VehicleSeriesDefinitionFormDefaultLayout} from './VehicleSeriesDefinitionFormDefaultLayout';
import {VehicleSeriesDefinitionFormWithLists} from './VehicleSeriesDefinitionFormWithLists';

export const VehicleSeriesDefinitionNew = () => {
  const navigate = useNavigate();
  const apiDispatch = useApiDispatch();

  const handleSubmit = useCallback(
    async (
      values: VehicleSeriesDefinitionFormType,
      _setErrors: any, // eslint-disable-line typescript/no-explicit-any -- Pre-existing any
      blockerContext?: BlockerContext
    ) => {
      const fullValues = values as z.infer<typeof VehicleSeriesDefinitionFormValidations>;
      const requestBody: VehicleSeriesDefinitionRequestBody = {
        ...fullValues,
        vehicleType: fullValues.vehicleType as VehicleSeriesDefinitionRequestBody['vehicleType'],
      };
      await apiDispatch(createVehicleSeriesDefinition.action, {
        requestBody,
      })
        .then(() => {
          showNotification.success(
            i18n.t('page.vehicleSeriesDefinition.notifications.definitionCreated')
          );
          if (blockerContext?.wasSubmittedFromBlocker) {
            return;
          }
          navigate(settingsRoutes.vehicleSeriesDefinition);
        })
        .catch((error: {response: Error}) =>
          handleApiError(error?.response, {signalApiError: blockerContext?.reportApiFailure})
        );
    },
    [navigate, apiDispatch]
  );

  return (
    <VehicleSeriesDefinitionFormDefaultLayout>
      <VehicleSeriesDefinitionFormWithLists
        cancelRoute={settingsRoutes.vehicleSeriesDefinition}
        onSubmit={handleSubmit}
        renderFooter={({navigateWithoutBlocker, cancelRoute}) => (
          <>
            <Separator />
            <VehicleSeriesDefinitionFormButtons
              onCancel={cancelRoute ? () => navigateWithoutBlocker(cancelRoute) : undefined}
              submitLabel={i18n.t('general.actions.create')}
              cancelTestId={testIds.settings.vehicleNewSeriesDefinition('cancel')}
              submitTestId={testIds.settings.vehicleNewSeriesDefinition('create')}
            />
          </>
        )}
      />
    </VehicleSeriesDefinitionFormDefaultLayout>
  );
};
